<template>
  <div>
    <div>
      <FilterManager
        v-model="filters"
        :filters="filterSettings"
        @academicYear="onAcademicYearChanged"
        @center="onCenterChanged"
        @municipality="onMunicipalityChanged"
        @school="onSchoolChanged"
        @search="onSearch"
      />
    </div>
    <div>
      <CustomResultNotFound
        v-if="!results"
        class="mt-4 row pt-3"
        text="No se ha encontrado resultados."
      />
      <div v-else>
        <RequestResultRow
          v-for="item in results"
          :key="item.id"
          :value="item"
          :assistants="item.assignmentOptions"
          @updateSearch="updatetoCancel"
          @changeStatus="changeRequestStatus"
          @go-to-detail="persistFilters"
          @listIds="sendListIds"
        />
        <pagination
          v-if="pagination.totalItemCount > pagination.perPage"
          :data="pagination"
          @pagination-go-page="onPageChanged"
        />
      </div>
    </div>
  </div>
</template>

<script>
import FilterManager from "FilterManager";
import CustomResultNotFound from "custom-result-not-found";
import RequestResultRow from "./RequestResultRow.vue";
import Pagination from "pagination";
import locationService from "@/services/api/location.api.js";
import mixAlert from "@/mixin/alert.mixin";
import { requestStatusCodes } from "@/utils/constants/hrrequest/requestStatusCodes";
import academicYears from "@/utils/constants/academicYears";

import { GetAssistantList } from "@/modules/peiSAEE/api.js";
import {
  getRequests,
  changeRequestStatusToPrevious,
  updateRequestStatus
} from "@/modules/human-resources/api";

const FILTERS_KEY = "irregularRequestFiltersStorage";
const PAGINATION_KEY = "irregularRequestPaginationStorage";
const KEY_CHECKBOX='CHECKBOXSTATUSSTORAGE'

export default {
  name: "IrregularRequests",
  mixins: [mixAlert],
  components: {
    FilterManager,
    CustomResultNotFound,
    RequestResultRow,
    Pagination
  },
  props: {
    collections: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      filters: {
        academicYear: null,
        status: null,
        searchField: null,
        center: null,
        municipality: null,
        school: null,
        requestType: null,
        necesity: null,
        ubicationAlternative: null,
        route: null,
        assistant: null
      },
      centers: [],
      municipalities: [],
      schools: [],
      assistants: [],
      results: [],
      pagination: {
        perPage: 10,
        currentPage: 1,
        totalItemCount: 0,
        pageCount: 0,
        count: 0
      }
    };
  },
  computed: {
    filterStatus() {
      let statusList =
        this.filters.academicYear?.id >= academicYears.Y2023_2024
          ? [
              // New List
              requestStatusCodes.new,
              requestStatusCodes.submitted,
              requestStatusCodes.returnedToSchool,
              requestStatusCodes.SAEEAnalysisProcess,
              requestStatusCodes.returnedToCSEEDirector,
              requestStatusCodes.recomended,
              requestStatusCodes.notRecomended,              
              requestStatusCodes.Cancelada,
              requestStatusCodes.ApprovedProvisionalRemedy,
              requestStatusCodes.NotRecommendedPEIAmendment,
              requestStatusCodes.NotCurrentDischarge
            ]
          : [
              // Old List
              requestStatusCodes.new,
              requestStatusCodes.submitted,
              requestStatusCodes.teacherFacilitatorAnalysisProcess,
              requestStatusCodes.returnedToSchool,
              requestStatusCodes.pendingCSEEAnalysis,
              requestStatusCodes.CSEEAnalysisProcess,
              requestStatusCodes.returnedToFacilitatorTeacher,
              requestStatusCodes.SAEEPendingAnalysis,
              requestStatusCodes.SAEEAnalysisProcess,
              requestStatusCodes.returnedToCSEEDirector,
              requestStatusCodes.recomended,
              requestStatusCodes.notRecomended,
              requestStatusCodes.RecommendedExistingResource,
              requestStatusCodes.Cancelada
            ];

      return this.collections.requestsStatus.filter(x =>
        statusList.includes(x.id)
      );
    },
    filterSettings() {
      return [
        {
          name: "academicYear",
          component: "CustomDropDown",
          options: this.collections.academicYears,
          value: this.filters.academicYear,
          clearable: false,
          placeholder: "Año Académico",
          fieldName: "description",
          fieldKey: "id",
          class: "col-sm-12 col-md-3 mb-md-0 mb-2"
        },
        {
          name: "status",
          component: "CustomDropDown",
          options: this.filterStatus,
          value: this.filters.status,
          placeholder: "Estatus",
          fieldName: "name",
          fieldKey: "id",
          class: "col-sm-12 col-md-3 mb-md-0 mb-2"
        },
        {
          name: "searchField",
          component: "CustomInput",
          value: this.filters.searchField,
          placeholder:
            "Escriba Número de SIE, Registro, Solicitud o Nombre del Estudiante",
          icon: "fa fa-search orange-icon",
          leftIcon: true,
          class: "col-12 col-sm-12 col-md-6",
          type: "text"
        },
        {
          name: "center",
          label: "Centro:",
          component: "CustomDropDown",
          options: this.centers,
          value: this.filters.center,
          placeholder: "Seleccione",
          class: "col-12 col-sm-6 col-md-3 d-flex flex-column",
          fieldName: "name",
          fieldKey: "id",
          mapParentValue: this.filters.center == null,
          isAdvanced: true,
          disabled: false
        },
        {
          name: "municipality",
          label: "Municipio:",
          component: "CustomDropDown",
          options: this.municipalities,
          value: this.filters.municipality,
          placeholder: "Seleccione",
          class: "col-12 col-sm-6 col-md-3 d-flex flex-column",
          fieldName: "name",
          fieldKey: "id",
          mapParentValue: this.filters.municipality == null,
          isAdvanced: true,
          disabled: false
        },
        {
          name: "school",
          label: "Escuela:",
          component: "CustomDropDown",
          options: this.schools,
          value: this.filters.school,
          placeholder: "Seleccione",
          class: "col-12 col-sm-6 col-md-3 d-flex flex-column",
          fieldName: "name",
          fieldKey: "id",
          mapParentValue: this.filters.school == null,
          isAdvanced: true,
          disabled: false
        },
        {
          name: "requestType",
          label: "Tipo de Solicitud:",
          component: "CustomDropDown",
          options: this.collections.requestTypes,
          value: this.filters.determination,
          placeholder: "Seleccione",
          class: "col-12 col-sm-6 col-md-3 d-flex flex-column",
          fieldName: "name",
          fieldKey: "id",
          mapParentValue: this.filters.requestType == null,
          isAdvanced: true,
          disabled: false
        },
        {
          name: "necesity",
          label: "Necesidades:",
          component: "CustomDropDown",
          options: this.collections.necesities,
          value: this.filters.necesity,
          placeholder: "Seleccione",
          class: "col-12 col-sm-6 col-md-3 d-flex flex-column",
          fieldName: "name",
          fieldKey: "id",
          mapParentValue: this.filters.necesity == null,
          isAdvanced: true,
          disabled: false
        },
        {
          name: "ubicationAlternative",
          label: "Alternativas de Ubicación:",
          component: "CustomDropDown",
          options: this.collections.ubicationAlternatives,
          value: this.filters.ubicationAlternative,
          placeholder: "Seleccione",
          class: "col-12 col-sm-6 col-md-3 d-flex flex-column",
          fieldName: "name",
          fieldKey: "id",
          mapParentValue: this.filters.ubicationAlternative == null,
          isAdvanced: true,
          disabled: false
        },
        {
          name: "route",
          label: "Ruta:",
          component: "CustomDropDown",
          options: this.collections.routes,
          value: this.filters.route,
          placeholder: "Seleccione",
          class: "col-12 col-sm-6 col-md-3 d-flex flex-column",
          fieldName: "name",
          fieldKey: "id",
          mapParentValue: this.filters.route == null,
          isAdvanced: true,
          disabled: false
        },
        {
          name: "assistant",
          label: "Asistente:",
          component: "CustomDropDown",
          options: this.assistants,
          value: this.filters.assistant,
          placeholder: "Seleccione",
          class: "col-12 col-sm-6 col-md-3 d-flex flex-column",
          fieldName: "name",
          fieldKey: "id",
          mapParentValue: this.filters.assistant == null,
          isAdvanced: true,
          disabled: false
        }
      ];
    }
  },
  methods: {
    async loadCenters(academicYearId) {
      let id = academicYearId;
      if (!id) {
        id = this.filters.academicYear.id;
      }
      const { data } = await locationService.getCenters({
        academicYearId: id
      });
      this.centers = data;

      if (data != null && data.length == 1 && data[0].selected) {
        this.filters.center = data[0];
        await this.loadMunicipalities(this.filters.center.id);
      }
    },
    async loadMunicipalities(centerId) {
      const { data } = await locationService.getMunicipalities({
        centerId,
        academicYearId: this.filters.academicYear.id
      });
      this.municipalities = data;

      if (data != null && data.length == 1 && data[0].selected) {
        this.filters.municipality = data[0];
        await this.loadSchools(this.filters.municipality.id);
      }
    },
    async loadSchools(municipalityId) {
      const { data } = await locationService.getSchools({
        municipalityId,
        academicYearId: this.filters.academicYear.id
      });
      this.schools = data;

      if (data != null && data.length == 1 && data[0].selected) {
        this.filters.school = data[0];
        await this.loadAssistants(this.filters.school.id);
      }
    },
    async loadAssistants(schoolId) {
      const academicYearId = this.filters.academicYear.id;
      const { data } = await GetAssistantList(academicYearId, schoolId);
      this.assistants = data;
    },
    onAcademicYearChanged() {
      this.filters.status = null;
      this.filters.searchField = null;

      this.filters.center = this.centers.length === 1 ? this.centers[0] : null;
      this.filters.municipality =
        this.municipalities.length === 1 ? this.municipalities[0] : null;
      this.filters.school = this.schools.length === 1 ? this.schools[0] : null;

      this.filters.requestType = null;
      this.filters.necesity = null;
      this.filters.ubicationAlternative = null;
      this.filters.route = null;
      this.filters.assistant = null;

      if (this.filters.school) {
        this.loadAssistants(this.filters.school.id);
      }
    },
    onCenterChanged(center) {
      this.filters.municipality = null;
      this.municipalities = [];
      this.onMunicipalityChanged(); // Cleaning schools
      if (center) {
        this.loadMunicipalities(center.id);
      }
    },
    onMunicipalityChanged(municipality) {
      this.filters.school = null;
      this.schools = [];
      if (municipality) {
        this.loadSchools(municipality.id);
      }
    },
    onSchoolChanged(school) {
      this.filters.assistant = null;
      this.assistants = [];
      if (school) {
        this.loadAssistants(school.id);
      }
    },
    async updatetoCancel(info) {
      await updateRequestStatus(false, info.valueId, info.dto);
      await this.getResults(this.filters, this.pagination.currentPage);
    },
    async onSearch(filters, page) {
      sessionStorage.setItem( KEY_CHECKBOX, JSON.stringify([]))
      this.getResults(filters, page);
    },
    initResults(filters, page) {
      this.getResults(filters, page);
    },
    async getResults(filters, page = 1) {
      const mappedFilter = {
        academicYearId: filters.academicYear.id,
        statusId: filters.status?.id,
        searchField: filters.searchField,
        centerId: filters.center?.id,
        municipalityId: filters.municipality?.id,
        schoolId: filters.school?.id,
        assistantTypeId: filters.assistantType?.id,
        necesityId: filters.necesity?.id,
        ubicationAlternativeId: filters.ubicationAlternative?.id,
        assistantId: filters.assistant?.id,
        requestTypeId: filters.requestType?.id,
        pageIndex: page
      };

      await getRequests(mappedFilter).then(res => {
        this.results = res.data.data;        
        
        this.pagination = {
          ...this.pagination,
          totalItemCount: res.data.totalItemCount,
          pageCount: res.data.pageCount,
          currentPage: res.data.currentPage,
          count: res.data.count
        };
      });
      this.$emit('changeStatusData',filters)
      this.persistFilters()
    },
    async onPageChanged(currentPage) {
      this.pagination.currentPage = currentPage;
      await this.getResults(this.filters, currentPage);
    },
    async changeRequestStatus(requestId) {
      try {
        await changeRequestStatusToPrevious(requestId);

        this.ShowToast(
          "Confirmación",
          "La solicitud ha sido cambiada de estatus exitosamente.",
          "success"
        );

        await this.onSearch(this.filters, this.pagination.currentPage);
      } catch (error) {
        this.ShowToast(
          "Información Incompleta",
          error.response.data.message,
          "error"
        );
      }
    },
    persistFilters() {
      sessionStorage.setItem(FILTERS_KEY, JSON.stringify(this.filters));
      sessionStorage.setItem(PAGINATION_KEY, JSON.stringify(this.pagination));
    },
    sendListIds(data){
      this.$emit('getListIds',data)
    },
    async configurationResult(){
      const savedFilters = sessionStorage.getItem(FILTERS_KEY);
      const savedPagination = sessionStorage.getItem(PAGINATION_KEY);

      if (savedFilters) {
        this.filters = JSON.parse(savedFilters);
        sessionStorage.removeItem(FILTERS_KEY);
      } else {
        this.filters.academicYear =
          this.collections.academicYears.find(e => e.selected) ??
          this.collections.academicYears[0];
      }

      if (savedPagination) {
        this.pagination = JSON.parse(savedPagination);
        sessionStorage.removeItem(PAGINATION_KEY);
      }

      await this.loadCenters(this.filters.academicYear?.id);
      this.initResults(this.filters, this.pagination.currentPage);
    }
  },
  mounted() {
    this.configurationResult()
  }
};
</script>
